import type { CurrencyType } from '@wg/wows-react-uikit/components/Currency/Currency';

import type { Moment } from 'moment';
import type { InferActionsType } from '~/Reducers';
import type { RegularRewardsOffersCooldown, RegularRewardsType } from '~/Reducers/ReducerTreasury';
import type { TREASURY_TABS } from '~/constants';
import type {
  Balance,
  DayTransactions,
  IClanMember,
  Transaction,
  Transactions,
  TransactionUpdatingStatus,
} from '~/types/declaration';
import type { Nullable } from '~/types/generics';

export const APPEND_TRANSACTIONS = 'APPEND_TRANSACTIONS';
export const DISTRIBUTE_RESOURCES = 'DISTRIBUTE_RESOURCES';
export const FILTER_TRANSACTIONS = 'FILTER_TRANSACTIONS';
export const GET_TOTAL_BALANCE = 'GET_TOTAL_BALANCE';
export const GET_TOTAL_TRANSACTIONS_SUM = 'GET_TOTAL_TRANSACTIONS_SUM';
export const GET_TRANSACTIONS = 'GET_TRANSACTIONS';
export const GET_TREASURY_CONFIG = 'GET_TREASURY_CONFIG';
export const SET_DETAIL_VIEW_TRANSACTION = 'SET_DETAIL_VIEW_TRANSACTION';
export const UNSET_DETAIL_VIEW_TRANSACTION = 'UNSET_DETAIL_VIEW_TRANSACTION';
export const LISTEN_TRANSACTIONS_STATUS_UPDATES = 'LISTEN_TRANSACTIONS_STATUS_UPDATES';
export const SELECT_DISTRIBUTION_CURRENCY = 'SELECT_DISTRIBUTION_CURRENCY';
export const SET_DISTRIBUTION_FILTER = 'SET_DISTRIBUTION_FILTER';
export const SET_DISTRIBUTION_SUM = 'SET_DISTRIBUTION_SUM_DIRECTION';
export const SET_FILTER_DATES = 'SET_FILTER_DATES';
export const SET_IS_TRANSACTION_LIST_UPDATING = 'SET_IS_TRANSACTION_LIST_UPDATING';
export const SET_SELECTED_TAB = 'SET_SELECTED_TAB_TREASURY';
export const SET_SORT_FIELD = 'SET_SORT_FIELD';
export const SET_TRANSACTION_SUM_UPDATING = 'SET_TRANSACTION_SUM_UPDATING';
export const TOGGLE_DISTRIBUTION_MEMBERS = 'TOGGLE_DISTRIBUTION_MEMBERS';
export const TOGGLE_IS_DISTRIBUTION_FILTER_VISIBLE = 'TOGGLE_IS_DISTRIBUTION_FILTER_VISIBLE';
export const UPDATE_TREASURY_BOXES_COOLDOWN = 'UPDATE_TREASURY_BOXES_COOLDOWN';

export enum TRANSACTIONS_STATUSES {
  COMPLETED = 'completed',
  PENDING = 'pending',
  FAILED = 'failed',
}

export enum TRANSACTIONS_STATUSES_DISPLAYED {
  COMPLETED = TRANSACTIONS_STATUSES.COMPLETED,
  PENDING = TRANSACTIONS_STATUSES.PENDING,
  FAILED = TRANSACTIONS_STATUSES.FAILED,
  SEMI_COMPLETED = 'semiCompleted',
}

export const TRANSACTIONS_STATUSES_MAP = {
  committed: TRANSACTIONS_STATUSES.COMPLETED,
  completed: TRANSACTIONS_STATUSES.COMPLETED,
  failed: TRANSACTIONS_STATUSES.FAILED,
  in_progress: TRANSACTIONS_STATUSES.PENDING,
  pending: TRANSACTIONS_STATUSES.PENDING,
  rolled_back: TRANSACTIONS_STATUSES.FAILED,
} as const;

export type ActionsType = InferActionsType<typeof actionsTreasury>;

export const actionsTreasury = {
  setTransactionSumUpdating: () =>
    ({
      type: SET_TRANSACTION_SUM_UPDATING,
    }) as const,

  distributeResources: () =>
    ({
      type: DISTRIBUTE_RESOURCES,
    }) as const,

  filterTransactions: (transactions: Transactions) =>
    ({
      type: FILTER_TRANSACTIONS,
      payload: { transactions },
    }) as const,

  selectDistributionCurrency: (type: CurrencyType) =>
    ({
      type: SELECT_DISTRIBUTION_CURRENCY,
      payload: { type },
    }) as const,

  setDistributionFilter: (value: string) =>
    ({
      type: SET_DISTRIBUTION_FILTER,
      payload: {
        value,
      },
    }) as const,

  setDistributionSum: (sum: number) =>
    ({
      type: SET_DISTRIBUTION_SUM,
      payload: {
        sum,
      },
    }) as const,

  toggleDistributionMembers: (members: number[], toggleAll: boolean) =>
    ({
      type: TOGGLE_DISTRIBUTION_MEMBERS,
      payload: {
        members,
        toggleAll,
      },
    }) as const,

  setIsTransactionsUpdating: (isUpdating: boolean) =>
    ({
      type: SET_IS_TRANSACTION_LIST_UPDATING,
      payload: {
        isUpdating,
      },
    }) as const,

  setDetailViewTransaction: (transaction: Transaction) =>
    ({
      type: SET_DETAIL_VIEW_TRANSACTION,
      payload: {
        transaction: transaction,
      },
    }) as const,

  unsetDetailViewTransaction: () =>
    ({
      type: UNSET_DETAIL_VIEW_TRANSACTION,
    }) as const,

  toggleIsDistributionFilterVisible: () =>
    ({
      type: TOGGLE_IS_DISTRIBUTION_FILTER_VISIBLE,
    }) as const,

  setSortField: (field: Nullable<keyof IClanMember>) =>
    ({
      type: SET_SORT_FIELD,
      payload: {
        sortField: field,
      },
    }) as const,

  getTotalTransactionsSum: (data: Balance) =>
    ({
      type: GET_TOTAL_TRANSACTIONS_SUM,
      payload: {
        totalTransactionsSum: data
          .filter((item) => item.amount > 0)
          .map((item) => {
            return {
              type: item.type,
              amount: item.amount,
            };
          }),
      },
    }) as const,

  getTotalBalance: (totalBalanceList: Balance) =>
    ({
      type: GET_TOTAL_BALANCE,
      payload: { totalBalanceList },
    }) as const,

  setFilterDates: (start: Moment | null, end: Moment | null) =>
    ({
      type: SET_FILTER_DATES,
      payload: { start, end },
    }) as const,

  setSelectedTab: (index: TREASURY_TABS) =>
    ({
      type: SET_SELECTED_TAB,
      payload: { index },
    }) as const,

  getTransactions: (transactions: DayTransactions[], lastOffet: number) =>
    ({
      type: GET_TRANSACTIONS,
      payload: { transactions, lastOffet },
    }) as const,

  listenTransactionsStatusUpdates: (data: TransactionUpdatingStatus[]) =>
    ({
      type: LISTEN_TRANSACTIONS_STATUS_UPDATES,
      payload: { data },
    }) as const,

  appendTransactions: () =>
    ({
      type: APPEND_TRANSACTIONS,
      payload: { isAppending: true },
    }) as const,

  getTreasuryConfig: (config: RegularRewardsType) =>
    ({
      type: GET_TREASURY_CONFIG,
      payload: config,
    }) as const,

  updateTreasuryBoxesCoolDown: (data: RegularRewardsOffersCooldown) =>
    ({
      type: UPDATE_TREASURY_BOXES_COOLDOWN,
      payload: data,
    }) as const,
};
